/**
 * El control de subir un fichero · §S.3
 *
 * ## Un botón, no un `<input type="file">` a la vista
 *
 * El control nativo enseña «Ningún archivo seleccionado» en el idioma del navegador y
 * no se puede estilar. Lo que se ve es un botón de la casa; el `input` está detrás y
 * es el que hace el trabajo.
 *
 * ## Se dice lo que cabe **antes** de elegir
 *
 * «PNG o SVG, hasta 2 MB». Los límites vienen de `LIMITES`, que son los del contrato,
 * y el `accept` del input filtra el diálogo del sistema. El servidor lo comprueba
 * igualmente: esto no es la regla, es no hacer esperar a alguien que está subiendo
 * ocho megas por la red del polideportivo para decirle al final que no valía.
 *
 * ## El `input` se vacía después de cada subida
 *
 * Sin eso, elegir **el mismo fichero** dos veces seguidas no dispara `change` —el
 * valor no ha cambiado— y el segundo intento parece que no hace nada. Pasa justo
 * cuando la primera vez falló y la persona vuelve a probar con el mismo.
 */
import { useRef, useState } from 'react';
import {
  aceptaDe,
  LIMITES,
  subirFichero,
  type Asset,
  type UsoDeFichero,
} from '@/shared/api/subidas';
import { Error as PanelError } from '@/shared/ui/Estados';
import type { Problema } from '@/shared/api/errores';

const ETIQUETAS: Readonly<Record<string, string>> = {
  'image/jpeg': 'JPG',
  'image/png': 'PNG',
  'image/webp': 'WebP',
  'image/svg+xml': 'SVG',
  'application/pdf': 'PDF',
};

export function SubirFichero({
  uso,
  club,
  clase,
  etiqueta,
  deshabilitado = false,
  alSubir,
}: {
  uso: UsoDeFichero;
  club?: string | undefined;
  clase?: string | undefined;
  /** Lo que dice el botón: «Subir el logo», «Adjuntar el PDF». */
  etiqueta: string;
  deshabilitado?: boolean;
  alSubir: (asset: Asset) => void | Promise<void>;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [subiendo, setSubiendo] = useState(false);
  const [problema, setProblema] = useState<Problema | null>(null);

  const limite = LIMITES[uso];
  const tipos = limite.tipos.map((t) => ETIQUETAS[t] ?? t).join(' o ');

  return (
    <>
      <div className="acciones">
        <button
          type="button"
          className="btn-plano"
          disabled={deshabilitado || subiendo}
          onClick={() => input.current?.click()}
        >
          {subiendo ? 'Subiendo…' : etiqueta}
        </button>
        <span className="tenue">
          {tipos}, hasta {limite.megas} MB
        </span>
      </div>

      <input
        ref={input}
        type="file"
        className="visualmente-oculto"
        accept={aceptaDe(uso)}
        tabIndex={-1}
        onChange={(e) => {
          const fichero = e.target.files?.[0];
          if (!fichero) return;

          setProblema(null);
          setSubiendo(true);

          void (async () => {
            const r = await subirFichero({ uso, fichero, club, clase });
            setSubiendo(false);

            // Se vacía siempre: ver la cabecera.
            if (input.current) input.current.value = '';

            if ('problema' in r) {
              setProblema(r.problema);
              return;
            }

            await alSubir(r.asset);
          })();
        }}
      />

      {problema && <PanelError problema={problema} />}
    </>
  );
}
